import React, { useMemo, useState } from 'react';
import {
    Box, Button, Typography, Table, TableHead, TableBody, TableRow, TableCell,
    TableContainer, Select, MenuItem, IconButton, CircularProgress,
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import { useSelector } from 'react-redux';

import { RootState } from '../../store';
import { AuditLogExplorerService } from '../../services/auditLogExplorer.service';
import { AuditLogExplorerTypes } from '../../types/auditLogExplorer.types';
import { ROWS_PER_PAGE_OPTIONS } from '../../builders/audit-log-explorer-builders';
import { formatSql } from './raw-sql-format';
import { AuditDetailDrawer } from './audit-detail-drawer';
import { ALE } from './tokens';

// Real warehouse schema: table `AuditLogs`, camelCase columns (see backend
// AUDIT_LIST_COLUMNS) — NOT the snake_case names from the design mock.
// `mongoId` is included so rows are clickable into the detail drawer out of the box.
const DEFAULT_QUERY = `SELECT mongoId, timestamp, method, statusCode, endpoint, responseTimeMs, subscriptionId
FROM AuditLogs
WHERE statusCode >= 200
  AND timestamp > NOW() - INTERVAL 1 HOUR
ORDER BY timestamp DESC
LIMIT 200;`;

// Execution limits enforced by the backend (API_CONTRACT §3). Kept as constants
// so the helper line and the timeout error copy stay in sync.
const RAW_SQL_TIMEOUT_SECONDS = 15;
const RAW_SQL_ROW_CAP = 10000;

// Guard/exec codes (API_CONTRACT §3) → friendly copy. Unknown codes show raw.
const ERROR_COPY: Record<string, string> = {
    RAW_SQL_EMPTY: 'Query is empty after stripping comments.',
    RAW_SQL_MULTIPLE_STATEMENTS: 'Only a single statement is allowed — remove the extra ";".',
    RAW_SQL_ONLY_SELECT_ALLOWED: 'Only SELECT (or WITH) statements are allowed.',
    RAW_SQL_FORBIDDEN_KEYWORD: 'Statement contains a forbidden keyword (DML/DDL/INTO/…).',
    RAW_SQL_EXECUTION_FAILED: 'SQL error — check syntax, table, and column names.',
    RAW_SQL_TIMEOUT: `Query exceeded the ~${RAW_SQL_TIMEOUT_SECONDS}s execution limit. Narrow the time range or add a tighter filter.`,
    USER_MUST_BE_POM_MASTER: 'You must be a POM Master to run queries.',
};
const friendlyError = (msg?: string) => (msg && ERROR_COPY[msg]) || msg || 'Query failed.';

const renderCell = (value: unknown): string => {
    if (value === null || value === undefined) return '—';
    if (typeof value === 'object') return JSON.stringify(value);
    return String(value);
};

const headCellSx = {
    bgcolor: ALE.headerStrip, fontWeight: 600, fontSize: '11px', letterSpacing: '.5px',
    textTransform: 'uppercase' as const, color: ALE.tMuted, padding: '11px 14px',
    borderBottom: `1px solid ${ALE.border}`, whiteSpace: 'nowrap' as const,
    fontFamily: ALE.mono,
};
const bodyCellSx = {
    padding: '9px 14px', borderBottom: `1px solid ${ALE.border}`, color: ALE.tSecondary,
    whiteSpace: 'nowrap' as const, fontSize: '12.5px', fontFamily: ALE.mono,
};

export const RawSqlConsole: React.FC<{ active?: boolean }> = ({ active = true }) => {
    const { detail, loading: storeLoading } = useSelector((s: RootState) => s.auditLogExplorer);

    const [sql, setSql] = useState(DEFAULT_QUERY);
    const [running, setRunning] = useState(false);
    const [result, setResult] = useState<AuditLogExplorerTypes.RawSqlResult | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [page, setPage] = useState(0);
    const [perPage, setPerPage] = useState(50);

    const [drawerOpen, setDrawerOpen] = useState(false);
    const [selectedMongoId, setSelectedMongoId] = useState<string | null>(null);

    // When this tab is hidden, close the detail drawer — it renders in a body-level
    // portal that would otherwise stay visible over the other tab.
    React.useEffect(() => {
        if (!active) { setDrawerOpen(false); setSelectedMongoId(null); }
    }, [active]);

    const run = async () => {
        if (running || !sql.trim()) return;
        setRunning(true);
        setError(null);
        const res = await AuditLogExplorerService.runRawSql(sql);
        if (res.ok && res.result) {
            setResult(res.result);
            setError(null);
            setPage(0);
        } else {
            setResult(null);
            setError(friendlyError(res.message));
        }
        setRunning(false);
    };

    const columns = result?.fields ?? [];
    // Row click can only open the full record when the query returned a mongoId
    // (the detail endpoint's key). Otherwise rows aren't clickable.
    const hasMongoId = columns.some((c) => c.name === 'mongoId');
    const pageRows = useMemo(
        () => (result ? result.rows.slice(page * perPage, page * perPage + perPage) : []),
        [result, page, perPage],
    );
    const start = result && result.rows.length ? page * perPage + 1 : 0;
    const end = result ? Math.min((page + 1) * perPage, result.rows.length) : 0;

    const openRow = (row: Record<string, unknown>) => {
        const id = row.mongoId;
        if (!hasMongoId || id == null) return;
        const mongoId = String(id);
        setSelectedMongoId(mongoId);
        setDrawerOpen(true);
        AuditLogExplorerService.getDetail(mongoId);
    };

    return (
        <Box>
            {/* read-replica banner */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', bgcolor: ALE.infoBg, color: ALE.infoT, borderRadius: '8px', p: '10px 14px', fontSize: '12.5px', fontWeight: 500, mt: '18px' }}>
                <InfoOutlinedIcon sx={{ fontSize: 17 }} />
                <span>Read-only (SELECT only) access to the audit-log warehouse — writes are impossible.</span>
            </Box>

            <Box sx={{ mt: '18px', bgcolor: ALE.cardBg, border: `1px solid ${ALE.border}`, borderRadius: '8px', boxShadow: ALE.shadowCard, overflow: 'hidden' }}>
                {/* editor */}
                <Box sx={{ p: '16px' }}>
                    <Typography sx={{ fontWeight: 600, fontSize: '13.5px', color: ALE.tPrimary, mb: '10px' }}>SQL Console</Typography>
                    <Box
                        component="textarea"
                        spellCheck={false}
                        value={sql}
                        onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setSql(e.target.value)}
                        onKeyDown={(e: React.KeyboardEvent) => { if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') run(); }}
                        sx={{
                            width: '100%', minHeight: 148, bgcolor: '#0f1b2d', color: '#d6e4f0',
                            border: `1px solid ${ALE.g300}`, borderRadius: '8px', padding: '14px 16px',
                            fontFamily: ALE.mono, fontSize: '13px', lineHeight: 1.65, resize: 'vertical',
                            tabSize: 2, outline: 'none', '&:focus': { borderColor: ALE.pMain, boxShadow: '0 0 0 3px rgba(17,167,226,.18)' },
                        }}
                    />
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', mt: '11px', flexWrap: 'wrap' }}>
                        <Typography sx={{ fontSize: '12px', color: ALE.tMuted, fontFamily: ALE.mono }}>
                            SELECT only · single statement · {RAW_SQL_TIMEOUT_SECONDS}s timeout · {RAW_SQL_ROW_CAP.toLocaleString()} row cap · read-only
                        </Typography>
                        <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: '9px' }}>
                            <Button
                                onClick={() => setSql((s) => formatSql(s))}
                                sx={{ height: 36, px: 2, borderRadius: '7px', textTransform: 'none', fontWeight: 600, fontSize: '13px', border: `1px solid ${ALE.g300}`, color: ALE.tSecondary, bgcolor: ALE.g0, '&:hover': { bgcolor: ALE.g100 } }}
                            >
                                Format
                            </Button>
                            <Button
                                onClick={run}
                                disabled={running || !sql.trim()}
                                startIcon={running ? <CircularProgress size={14} sx={{ color: '#fff' }} /> : <PlayArrowIcon sx={{ fontSize: '18px !important' }} />}
                                sx={{ height: 36, px: 2.5, borderRadius: '7px', textTransform: 'none', fontWeight: 600, fontSize: '13px', bgcolor: ALE.pMain, color: '#fff', '&:hover': { bgcolor: '#0f97cc' } }}
                            >
                                {running ? 'Running…' : 'Run'}
                            </Button>
                        </Box>
                    </Box>
                </Box>

                {/* error strip */}
                {error && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', m: '0 16px 16px', bgcolor: ALE.errorBg, color: ALE.errorT, borderRadius: '8px', p: '11px 14px', fontSize: '13px', fontWeight: 500 }}>
                        <ErrorOutlineIcon sx={{ fontSize: 17 }} />
                        <span>{error}</span>
                    </Box>
                )}

                {/* results caption */}
                {result && !error && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', p: '11px 16px', borderTop: `1px solid ${ALE.border}`, borderBottom: `1px solid ${ALE.border}`, bgcolor: ALE.headerStrip, fontSize: '12.5px', color: ALE.tMuted }}>
                        <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: ALE.successT }} />
                        <span>
                            Query OK — took <b style={{ color: ALE.tSecondary }}>{(result.durationMs / 1000).toFixed(2)}s</b>
                            {' · '}<b style={{ color: ALE.tSecondary }}>{result.rowCount.toLocaleString()} rows</b>
                            {' · '}{columns.length} columns
                            {result.truncated && <Box component="span" sx={{ ml: 1, color: ALE.warnT, fontWeight: 600 }}>· capped at {result.limit.toLocaleString()} — more rows may exist</Box>}
                        </span>
                        {result.rowCount > 0 && !hasMongoId && (
                            <Box component="span" sx={{ ml: 'auto', color: ALE.tFaint, fontStyle: 'italic' }}>
                                Add <Box component="code" sx={{ fontFamily: ALE.mono, color: ALE.tMuted }}>mongoId</Box> to your SELECT to open the full record on row click.
                            </Box>
                        )}
                    </Box>
                )}

                {/* results table */}
                {result && !error && (
                    result.rowCount === 0 ? (
                        <Box sx={{ p: '40px', textAlign: 'center', color: ALE.tMuted, fontSize: 13 }}>Query returned no rows.</Box>
                    ) : (
                        <>
                            <TableContainer sx={{ overflowX: 'auto' }}>
                                <Table sx={{ borderCollapse: 'collapse' }}>
                                    <TableHead>
                                        <TableRow>
                                            {columns.map((c, i) => <TableCell key={`${c.name}-${i}`} sx={headCellSx}>{c.name}</TableCell>)}
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {pageRows.map((row, ri) => {
                                            const selected = hasMongoId && selectedMongoId != null && String(row.mongoId) === selectedMongoId;
                                            return (
                                                <TableRow
                                                    key={ri}
                                                    onClick={hasMongoId ? () => openRow(row) : undefined}
                                                    sx={{
                                                        cursor: hasMongoId ? 'pointer' : 'default',
                                                        bgcolor: selected ? 'rgba(17,167,226,.07)' : 'transparent',
                                                        '&:hover': { bgcolor: hasMongoId ? (selected ? 'rgba(17,167,226,.07)' : ALE.rowHover) : 'transparent' },
                                                    }}
                                                >
                                                    {columns.map((c, ci) => {
                                                        const v = row[c.name];
                                                        return (
                                                            <TableCell key={ci} align={typeof v === 'number' ? 'right' : 'left'} sx={{ ...bodyCellSx, color: v === null || v === undefined ? ALE.tFaint : ALE.tSecondary }}>
                                                                {renderCell(v)}
                                                            </TableCell>
                                                        );
                                                    })}
                                                </TableRow>
                                            );
                                        })}
                                    </TableBody>
                                </Table>
                            </TableContainer>

                            {/* client-side pagination over returned rows */}
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: '16px', p: '11px 16px', fontSize: '12.5px', color: ALE.tMuted }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <span>Rows per page</span>
                                    <Select size="small" value={perPage} onChange={(e) => { setPerPage(Number(e.target.value)); setPage(0); }} sx={{ fontSize: '12.5px', '& .MuiSelect-select': { padding: '4px 24px 4px 10px' }, '& .MuiOutlinedInput-notchedOutline': { borderColor: ALE.g300 } }}>
                                        {ROWS_PER_PAGE_OPTIONS.map((n) => <MenuItem key={n} value={n} sx={{ fontSize: '12.5px' }}>{n}</MenuItem>)}
                                    </Select>
                                </Box>
                                <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: '14px' }}>
                                    <span>{start.toLocaleString()}–{end.toLocaleString()} of {result.rowCount.toLocaleString()}</span>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
                                        <IconButton size="small" disabled={page <= 0} onClick={() => setPage((p) => p - 1)} sx={{ border: `1px solid ${ALE.g300}`, borderRadius: '6px' }}>
                                            <KeyboardArrowLeftIcon sx={{ fontSize: 16 }} />
                                        </IconButton>
                                        <IconButton size="small" disabled={end >= result.rowCount} onClick={() => setPage((p) => p + 1)} sx={{ border: `1px solid ${ALE.g300}`, borderRadius: '6px' }}>
                                            <KeyboardArrowRightIcon sx={{ fontSize: 16 }} />
                                        </IconButton>
                                    </Box>
                                </Box>
                            </Box>
                        </>
                    )
                )}
            </Box>

            <AuditDetailDrawer
                open={drawerOpen}
                loading={storeLoading.detail}
                detail={detail}
                onClose={() => { setDrawerOpen(false); setSelectedMongoId(null); }}
            />
        </Box>
    );
};
